import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Card, Form, Button, Row, Col, Alert, Spinner, Modal } from "react-bootstrap";
import { useAuth } from "../../Components/AuthContext";
import { Pencil, X, Save, KeyRound, Trash2 } from "lucide-react";
import MainLayout from "../../Components/Layout/MainLayout/MainLayout";

const UserProfile = () => {
  const { tenantDomain, userId } = useParams();
  const { user, updateTenantDomain } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
    verificationKey: ""
  });
  const [passwordError, setPasswordError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    updateTenantDomain(tenantDomain);
    fetchUserProfile();
  }, [tenantDomain, userId]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/user/${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error(`Error fetching user profile: ${response.status}`);
      }

      const data = await response.json();
      setProfile(data);
      setEditForm({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        active: data.active
      });
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setError("Failed to load user profile. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing
      setEditForm({
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        active: profile.active
      });
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setEditForm({
      ...editForm,
      [e.target.name]: value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/user/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...editForm,
          userId: profile.userId,
          userRoles: profile.userRoles
        }),
      });

      if (!response.ok) {
        throw new Error(`Error updating profile: ${response.status}`);
      }

      await fetchUserProfile();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setError("Failed to update profile. Please try again.");
    }
  };

  const handleResetPasswordRequest = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trigger-reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ email: profile.email }),
      });

      if (!response.ok) {
        throw new Error(`Error requesting password reset: ${response.status}`);
      }

      setPasswordResetSent(true);
      setIsResettingPassword(true);
      setPasswordError(null);
    } catch (error) {
      console.error("Error requesting password reset:", error);
      setPasswordError("Failed to send password reset email. Please try again.");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    // Validate passwords match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    // Validate verification key is provided
    if (!passwordForm.verificationKey.trim()) {
      setPasswordError("Verification key is required");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: profile.email,
          verificationKey: passwordForm.verificationKey,
          newPassword: passwordForm.newPassword
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Error resetting password: ${errorData || response.status}`);
      }

      // Reset form and show success
      setPasswordForm({
        newPassword: "",
        confirmPassword: "",
        verificationKey: ""
      });
      setIsResettingPassword(false);
      setPasswordResetSent(false);
      setError(null);
      alert("Password has been successfully reset");
    } catch (error) {
      console.error("Error resetting password:", error);
      setPasswordError("Failed to reset password. Please check your verification key and try again.");
    }
  };
  
  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteConfirmation("");
    setDeleteError(null);
  };
  
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };
  
  const handleDeleteAccount = async () => {
    // Validate confirmation text
    if (deleteConfirmation !== "DELETE") {
      setDeleteError("Please type DELETE to confirm account deletion");
      return;
    }
    
    setIsDeletingAccount(true);
    setDeleteError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/${userId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include"
      });
      
      if (!response.ok) {
        const errorData = await response;
        throw new Error(errorData.message || "Failed to delete account");
      }
      
      // Clear local storage and redirect to login
      localStorage.removeItem("user");
      navigate(`/${tenantDomain}/login`, { 
        state: { message: "Your account has been deleted successfully" }
      });
    } catch (error) {
      console.error("Error deleting account:", error);
      setDeleteError(error.message || "An error occurred while trying to delete your account");
      setIsDeletingAccount(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Container className="d-flex justify-content-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Container>
      </MainLayout>
    );
  }

  if (error && !profile) {
    return (
      <MainLayout>
        <Container className="mt-5">
          <Alert variant="danger">{error}</Alert>
          <Button variant="primary" onClick={() => navigate(`/${tenantDomain}/users`)}>
            Back to Users
          </Button>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
    <Container className="mt-5">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h3>User Profile</h3>
          <div>
            <Button 
              variant={isEditing ? "secondary" : "primary"} 
              onClick={handleEditToggle}
              className="me-2"
            >
              {isEditing ? (
                <>
                  <X size={16} className="me-1" /> Cancel
                </>
              ) : (
                <>
                  <Pencil size={16} className="me-1" /> Edit
                </>
              )}
            </Button>
            {!isEditing && !isResettingPassword && (
              <Button 
                variant="warning" 
                onClick={handleResetPasswordRequest}
              >
                <KeyRound size={16} className="me-1" /> Reset Password
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
          
          {profile && (
            <Row>
              <Col md={isResettingPassword ? 8 : 12}>
                <Form>
                  {/* Profile Actions */}
                  <div className="mb-4 d-flex justify-content-end">
                    <Button 
                      variant="danger" 
                      className="ms-2"
                      onClick={handleShowDeleteModal}
                      disabled={isEditing}
                    >
                      <Trash2 size={16} className="me-1" /> Delete Account
                    </Button>
                  </div>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    {isEditing ? (
                      <Form.Control
                        type="email"
                        name="email"
                        value={editForm.email}
                        onChange={handleChange}
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={profile.email}
                        readOnly
                        plaintext
                      />
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    {isEditing ? (
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={editForm.firstName}
                        onChange={handleChange}
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={profile.firstName}
                        readOnly
                        plaintext
                      />
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    {isEditing ? (
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={editForm.lastName}
                        onChange={handleChange}
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={profile.lastName}
                        readOnly
                        plaintext
                      />
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="user-active"
                      label="Active"
                      name="active"
                      checked={isEditing ? editForm.active : profile.active}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Roles</Form.Label>
                    <div>
                      {profile.userRoles && profile.userRoles.map(role => (
                        <span key={role} className="badge bg-primary me-1">{role}</span>
                      ))}
                    </div>
                  </Form.Group>

                  {isEditing && (
                    <Button variant="success" onClick={handleSave}>
                      <Save size={16} className="me-1" /> Save Changes
                    </Button>
                  )}
                </Form>
              </Col>
              
              {isResettingPassword && (
                <Col md={4}>
                  <Card>
                    <Card.Header>Reset Password</Card.Header>
                    <Card.Body>
                      {passwordResetSent && (
                        <Alert variant="info">
                          A verification code has been sent to your email address.
                        </Alert>
                      )}
                      
                      {passwordError && (
                        <Alert variant="danger">
                          {passwordError}
                        </Alert>
                      )}
                      
                      <Form onSubmit={handleResetPassword}>
                        <Form.Group className="mb-3">
                          <Form.Label>Verification Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="verificationKey"
                            value={passwordForm.verificationKey}
                            onChange={handlePasswordChange}
                            required
                          />
                          <Form.Text className="text-muted">
                            Enter the verification code sent to your email
                          </Form.Text>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>New Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="newPassword"
                            value={passwordForm.newPassword}
                            onChange={handlePasswordChange}
                            required
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>Confirm Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="confirmPassword"
                            value={passwordForm.confirmPassword}
                            onChange={handlePasswordChange}
                            required
                          />
                        </Form.Group>
                        
                        <div className="d-flex justify-content-between">
                          <Button variant="success" type="submit">
                            Reset Password
                          </Button>
                          <Button 
                            variant="secondary" 
                            onClick={() => {
                              setIsResettingPassword(false);
                              setPasswordResetSent(false);
                              setPasswordForm({
                                newPassword: "",
                                confirmPassword: "",
                                verificationKey: ""
                              });
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
    
    {/* Delete Account Confirmation Modal */}
    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
      <Modal.Header closeButton>
        <Modal.Title className="text-danger">Delete Account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="danger">
          <p><strong>Warning:</strong> This action cannot be undone.</p>
          <p>Deleting your account will:</p>
          <ul>
            <li>Remove all your personal information</li>
            <li>Remove your access to all tenant organizations</li>
            <li>Delete your roles and permissions</li>
          </ul>
        </Alert>
        
        {deleteError && (
          <Alert variant="danger">
            {deleteError}
          </Alert>
        )}
        
        <Form.Group className="mb-3">
          <Form.Label>Type <strong>DELETE</strong> to confirm:</Form.Label>
          <Form.Control
            type="text"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            disabled={isDeletingAccount}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeletingAccount}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleDeleteAccount} 
          disabled={isDeletingAccount || deleteConfirmation !== "DELETE"}
        >
          {isDeletingAccount ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Deleting...
            </>
          ) : (
            "Delete Account"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
    </MainLayout>
  );
};

export default UserProfile;
